import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ============================================================================
// todo-client-mod -- TodoListItem
//
// 单条 Todo 展示组件：复选框 + 标题 + 截止日期 + 优先级 + 标签 + 操作菜单
// 带有悬浮反馈、完成动画和响应式适配。
// ============================================================================
import * as React from 'react';
import { useTheme } from '../styles/use-theme.js';
import { useResponsive } from '../styles/use-responsive.js';
import { priorityColors, radius, typography } from '../styles/theme.js';
import { CheckboxCheckedIcon, CheckboxIcon, CalendarIcon, TagIcon, MoreIcon, EditIcon, TrashIcon } from './icons.js';
import { TodoStatusBadge } from './todo-status-badge.js';
const PRIORITY_LABELS = {
    low: '低',
    medium: '中',
    high: '高',
    urgent: '紧急',
};
export function TodoListItem({ todo, onEdit, onDelete, onComplete }) {
    const { theme } = useTheme();
    const { isMobile } = useResponsive();
    const [showMenu, setShowMenu] = React.useState(false);
    const [completing, setCompleting] = React.useState(false);
    const isCompleted = todo.status === 'completed';
    const isOverdue = todo.dueDate && !isCompleted && todo.dueDate < Date.now();
    const formatDate = (ts) => {
        const d = new Date(ts);
        return `${d.getMonth() + 1}/${d.getDate()}`;
    };
    const handleComplete = () => {
        setCompleting(true);
        onComplete?.(todo);
        setTimeout(() => setCompleting(false), 400);
    };
    const containerStyle = {
        display: 'flex',
        alignItems: 'flex-start',
        gap: isMobile ? '8px' : '10px',
        padding: isMobile ? '10px' : '12px',
        backgroundColor: theme.bg.secondary,
        borderRadius: radius.md,
        border: `1px solid ${theme.border.default}`,
        transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s, opacity 0.3s',
        cursor: 'default',
        opacity: completing ? 0.8 : 1,
        animation: 'todo-fade-in-up 0.25s ease forwards',
    };
    const hoverStyle = {
        borderColor: theme.border.hover,
        boxShadow: theme.shadow.sm,
        transform: 'translateY(-1px)',
    };
    const handleMouseEnter = (e) => {
        Object.assign(e.currentTarget.style, hoverStyle);
    };
    const handleMouseLeave = (e) => {
        e.currentTarget.style.borderColor = theme.border.default;
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'none';
    };
    return (_jsxs("div", { style: containerStyle, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: [_jsx("button", { onClick: handleComplete, style: {
                    flexShrink: 0,
                    padding: 0,
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    color: isCompleted ? theme.status.success : theme.text.tertiary,
                    lineHeight: 1,
                    marginTop: '2px',
                    transition: 'color 0.2s, transform 0.2s',
                    transform: completing ? 'scale(1.3)' : 'scale(1)',
                }, title: isCompleted ? '取消完成' : '标记为完成', children: isCompleted ? React.createElement(CheckboxCheckedIcon) : React.createElement(CheckboxIcon) }), _jsxs("div", { style: { flex: 1, minWidth: 0, opacity: isCompleted ? 0.6 : 1, transition: 'opacity 0.3s' }, children: [_jsx("div", { style: {
                            fontSize: typography.fontSize.base,
                            fontWeight: typography.fontWeight.medium,
                            color: isCompleted ? theme.text.tertiary : theme.text.primary,
                            lineHeight: typography.lineHeight.normal,
                            wordBreak: 'break-word',
                            textDecoration: isCompleted ? 'line-through' : 'none',
                            transition: 'color 0.3s, text-decoration 0.3s',
                        }, children: todo.title }), _jsxs("div", { style: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginTop: '6px',
                            flexWrap: 'wrap',
                        }, children: [_jsx(TodoStatusBadge, { status: todo.status }), _jsxs("span", { style: {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    fontSize: typography.fontSize.xs,
                                    fontWeight: typography.fontWeight.medium,
                                    color: priorityColors[todo.priority],
                                    padding: `2px 6px`,
                                    borderRadius: radius.sm,
                                    backgroundColor: `${priorityColors[todo.priority]}18`,
                                    transition: 'background-color 0.15s',
                                }, children: [_jsx("span", { style: {
                                            display: 'inline-block',
                                            width: '6px',
                                            height: '6px',
                                            borderRadius: '50%',
                                            marginRight: '4px',
                                            backgroundColor: priorityColors[todo.priority],
                                        } }), PRIORITY_LABELS[todo.priority]] }), todo.dueDate && (_jsxs("span", { style: {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    fontSize: typography.fontSize.xs,
                                    gap: '2px',
                                    color: isOverdue ? theme.status.danger : theme.text.tertiary,
                                    fontWeight: isOverdue ? typography.fontWeight.semibold : typography.fontWeight.normal,
                                }, children: [React.createElement(CalendarIcon), _jsx("span", { style: { marginLeft: '3px' }, children: formatDate(todo.dueDate) }), isOverdue && _jsx("span", { style: { marginLeft: '4px', fontSize: '11px', fontWeight: 600 }, children: "\u5DF2\u8FC7\u671F" })] })), todo.tags && todo.tags.length > 0 && (_jsxs("span", { style: {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: typography.fontSize.xs,
                                    color: theme.text.tertiary,
                                }, children: [React.createElement(TagIcon), todo.tags.slice(0, 3).map((tag, i) => (_jsx("span", { style: {
                                            padding: '1px 5px',
                                            backgroundColor: theme.bg.tertiary,
                                            borderRadius: radius.sm,
                                            fontSize: '10px',
                                            color: theme.text.secondary,
                                        }, children: tag }, i))), todo.tags.length > 3 && (_jsxs("span", { style: { fontSize: '11px', color: theme.text.tertiary }, children: ["+", todo.tags.length - 3] }))] }))] })] }), _jsxs("div", { style: { position: 'relative', flexShrink: 0 }, children: [_jsx("button", { onClick: () => setShowMenu(!showMenu), style: {
                            padding: '4px',
                            border: 'none',
                            backgroundColor: showMenu ? theme.interaction.hover : 'transparent',
                            cursor: 'pointer',
                            color: showMenu ? theme.text.secondary : theme.text.tertiary,
                            borderRadius: radius.sm,
                            transition: 'background-color 0.15s, color 0.15s',
                        }, title: "\u66F4\u591A\u64CD\u4F5C", children: React.createElement(MoreIcon) }), showMenu && React.createElement('div', {
                        style: {
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: '4px',
                            backgroundColor: theme.bg.elevated,
                            borderRadius: radius.md,
                            border: `1px solid ${theme.border.default}`,
                            boxShadow: theme.shadow.lg,
                            zIndex: 10,
                            minWidth: '110px',
                            padding: '4px',
                            animation: 'todo-slide-down 0.15s ease forwards',
                        },
                    }, React.createElement('button', {
                        onClick: () => { onEdit?.(todo); setShowMenu(false); },
                        style: {
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            padding: '6px 8px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            fontSize: '13px',
                            color: theme.text.secondary,
                            cursor: 'pointer',
                            borderRadius: radius.sm,
                            textAlign: 'left',
                            transition: 'background-color 0.12s',
                        },
                    }, React.createElement(EditIcon), React.createElement('span', { style: { marginLeft: '6px' } }, '编辑')), React.createElement('button', {
                        onClick: () => { onDelete?.(todo); setShowMenu(false); },
                        style: {
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            padding: '6px 8px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            fontSize: '13px',
                            color: theme.status.danger,
                            cursor: 'pointer',
                            borderRadius: radius.sm,
                            textAlign: 'left',
                            transition: 'background-color 0.12s',
                        },
                    }, React.createElement(TrashIcon), React.createElement('span', { style: { marginLeft: '6px' } }, '删除')))] })] }));
}
//# sourceMappingURL=todo-list-item.js.map